import 'regenerator-runtime/runtime';

import Vue from 'vue';
import * as Sentry from '@sentry/vue';

import VueApollo from 'vue-apollo';
import { ApiClient } from '@/ApiClient';

import Amplify, * as AmplifyModules from 'aws-amplify';
import { AmplifyPlugin } from 'aws-amplify-vue';
import { CognitoConfig } from '@/Cognito/config.js';
Amplify.configure(CognitoConfig);

import { Step, Steps } from 'element-ui';
import 'element-ui/lib/theme-chalk/index.css';

import { ConstantsPlugin } from '@/services/constants';

import App from './App.vue';
import router from './router';
import store from './store';

// Initialize Sentry error monitoring
Sentry.init({
  Vue,
  dsn: 'https://c7c6d71416402a8a46a381204e174290@o1388406.ingest.us.sentry.io/4510648266391552',
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration(),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, // Capture 100% of transactions (adjust for production)
  // Session Replay
  replaysSessionSampleRate: 0.1, // Sample 10% of sessions
  replaysOnErrorSampleRate: 1.0, // Sample 100% of sessions with errors
  // Environment
  environment: process.env.NODE_ENV,
  release: `eyejack-creator-web@${process.env.VUE_APP_VERSION || '0.0.0'}`,
  // Only send errors in production
  enabled: process.env.NODE_ENV === 'production',
  // Filter out known infrastructure errors that don't affect functionality
  beforeSend(event, hint) {
    const error = hint.originalException;
    
    // Filter out AWS MQTT/IoT connection errors
    // These are infrastructure issues that don't affect core app functionality
    if (error?.errorMessage?.includes('AMQJS') || 
        error?.errorCode === 7 ||
        error?.errorCode === 1 ||
        (typeof error === 'object' && error?.errorMessage?.includes('Connect timed out')) ||
        (typeof error === 'object' && error?.errorMessage?.includes('Socket error'))) {
      console.warn('[Sentry] Filtered MQTT error:', error?.errorMessage || error);
      return null; // Don't send to Sentry
    }
    
    // Filter out SharedArrayBuffer errors (Safari limitation, already handled in UI)
    if (event.message?.includes('SharedArrayBuffer') ||
        error?.message?.includes('SharedArrayBuffer')) {
      console.warn('[Sentry] Filtered SharedArrayBuffer error');
      return null;
    }
    
    // Filter out AWS S3 clock skew errors (client-side clock issue, already handled in UI)
    if (error?.code === 'RequestTimeTooSkewed' ||
        error?.message?.includes('RequestTimeTooSkewed') ||
        event.message?.includes('RequestTimeTooSkewed')) {
      console.warn('[Sentry] Filtered clock skew error (client clock issue)');
      return null;
    }
    
    // Filter out user cancellation errors (intentional user action, not a bug)
    if (error?.message === 'cancelled!' ||
        error?.message === 'cancelled during upload!' ||
        error?.message?.startsWith('cancelled') ||
        error?.message?.includes('FFmpeg.terminate()') ||
        event.message?.includes('FFmpeg.terminate()')) {
      console.log('[Sentry] Filtered user cancellation (not an error)');
      return null;
    }
    
    // Filter out file permission errors (client-side environment issue)
    // Occurs when file is moved/deleted/inaccessible after selection
    if (error?.name === 'NotReadableError' ||
        error?.message?.includes('NotReadableError') ||
        error?.message?.includes('could not be read')) {
      console.warn('[Sentry] Filtered file permission error (file moved/deleted after selection)');
      return null;
    }
    
    // Filter out Vue Router navigation cancellation errors
    // These occur when user triggers a new navigation before previous completes (e.g., double-click)
    if (error?.message?.includes('Navigation cancelled') ||
        error?.message?.includes('Navigation aborted') ||
        error?.message?.includes('Redirected when going from') ||
        error?.name === 'NavigationDuplicated') {
      console.log('[Sentry] Filtered navigation cancellation (expected behavior)');
      return null;
    }
    
    // Filter out chunk loading errors (stale client after deployment)
    // These are automatically handled by page reload in router.js or global handler
    if (error?.name === 'ChunkLoadError' ||
        error?.message?.includes('Loading chunk') ||
        error?.message?.includes('Loading CSS chunk') ||
        (error?.name === 'SyntaxError' && error?.message?.includes('Unexpected token'))) {
      console.log('[Sentry] Filtered chunk loading error (stale client, auto-reloading)');
      return null;
    }
    
    // Filter out vue-router lazy-load resolution failures (__esModule check on undefined)
    // Same root cause as chunk loading errors — stale client after deployment
    if (event.message?.includes('__esModule') ||
        error?.message?.includes('__esModule')) {
      console.log('[Sentry] Filtered lazy-load resolution error (stale client)');
      return null;
    }
    
    // Filter out Apollo store-reset errors during logout
    // Occurs when a GraphQL query/subscription is in flight while the user signs out
    if (error?.message?.includes('Store reset while query was in flight')) {
      console.log('[Sentry] Filtered Apollo store reset during logout');
      return null;
    }
    
    // Filter out 401 auth expiration errors (session expired, handled by router guard)
    if (error?.message?.includes('Received status code 401')) {
      console.log('[Sentry] Filtered 401 auth expiration error');
      return null;
    }
    
    // Filter out user-aborted requests (intentional navigation away)
    if (error?.name === 'AbortError' ||
        error?.message?.includes('The user aborted a request')) {
      console.log('[Sentry] Filtered user-aborted request');
      return null;
    }
    
    // Filter out iOS webkit messageHandlers errors (native bridge not available in web)
    if (error?.message?.includes('webkit.messageHandlers')) {
      console.log('[Sentry] Filtered webkit messageHandlers (native bridge unavailable)');
      return null;
    }
    
    // Filter out DynamoDB conditional request failures (backend race condition, non-actionable client-side)
    if (error?.message?.includes('conditional request failed') && error?.message?.includes('DynamoDb')) {
      console.log('[Sentry] Filtered DynamoDB conditional request error');
      return null;
    }
    
    // Filter out non-Error FFmpeg promise rejections (string values without .message property)
    const eventMessage = event.message || '';
    if (eventMessage.includes('FFmpeg') && (
        eventMessage.includes('terminate') ||
        eventMessage.includes('no output frames'))) {
      console.log('[Sentry] Filtered FFmpeg rejection');
      return null;
    }
    
    // Filter out browser extension interference ("Object Not Found Matching Id")
    if (eventMessage.includes('Object Not Found Matching Id') ||
        error?.message?.includes('Object Not Found Matching Id')) {
      console.log('[Sentry] Filtered browser extension error');
      return null;
    }
    
    return event;
  },
});

// Global handler for chunk loading errors (JS and CSS)
// This handles cases where a stale client tries to load old chunks after deployment
window.addEventListener('error', (event) => {
  const isChunkError = 
    event.error?.name === 'ChunkLoadError' ||
    event.message?.includes('Loading chunk') ||
    event.message?.includes('Loading CSS chunk');
  
  if (isChunkError) {
    // Check if we've already tried to reload (prevent infinite loops)
    const lastReload = sessionStorage.getItem('chunk_reload_timestamp');
    const now = Date.now();
    
    if (!lastReload || (now - parseInt(lastReload, 10)) > 10000) {
      sessionStorage.setItem('chunk_reload_timestamp', now.toString());
      window.location.reload();
    }
  }
}, true);

Vue.use(ConstantsPlugin);
Vue.use(Step);
Vue.use(Steps);
Vue.use(VueApollo);
Vue.use(AmplifyPlugin, AmplifyModules);

export const apolloProvider = new VueApollo({
  defaultClient: ApiClient,
});

export const apolloClient = apolloProvider.defaultClient;

Vue.config.productionTip = false;

// Global error handler for Vue components (Sentry will also capture these)
Vue.config.errorHandler = (err, vm, info) => {
  console.error('🔴 Vue Error:', err);
  console.error('Component:', vm?.$options?.name || 'Unknown');
  console.error('Info:', info);
  console.error('Stack:', err?.stack);
  // Sentry captures this automatically via its Vue integration
};

// Global handler for unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  
  // Quietly handle AWS MQTT/IoT connection errors (infrastructure issues)
  if (reason?.errorMessage?.includes('AMQJS') ||
      reason?.errorCode === 7 ||
      reason?.errorCode === 1 ||
      reason?.errorMessage?.includes('Connect timed out') ||
      reason?.errorMessage?.includes('Socket error')) {
    console.warn('[MQTT] Connection error (non-critical):', reason?.errorMessage || reason);
    event.preventDefault(); // Prevent default browser error handling
    return;
  }
  
  // Quietly handle SharedArrayBuffer errors (Safari limitation)
  if (reason?.message?.includes('SharedArrayBuffer') ||
      (typeof reason === 'string' && reason.includes('SharedArrayBuffer'))) {
    console.warn('[FFmpeg] SharedArrayBuffer not available (Safari limitation)');
    event.preventDefault();
    return;
  }
  
  // Quietly handle Vue Router navigation cancellation (expected behavior)
  if (reason?.message?.includes('Navigation cancelled') ||
      reason?.message?.includes('Navigation aborted') ||
      reason?.message?.includes('Redirected when going from') ||
      reason?.name === 'NavigationDuplicated') {
    event.preventDefault();
    return;
  }
  
  // Quietly handle FFmpeg termination (user cancelled processing)
  const reasonStr = typeof reason === 'string' ? reason : reason?.message || '';
  if (reasonStr.includes('cancelled') ||
      reasonStr.includes('FFmpeg.terminate()') ||
      reasonStr.includes('FFmpeg execution failed') ||
      reasonStr.includes('no output frames')) {
    console.log('[FFmpeg] User cancelled processing');
    event.preventDefault();
    return;
  }
  
  // Quietly handle Apollo store reset during logout
  if (reasonStr.includes('Store reset while query was in flight')) {
    event.preventDefault();
    return;
  }
  
  // Quietly handle 401 auth expiration
  if (reasonStr.includes('Received status code 401')) {
    event.preventDefault();
    return;
  }
  
  console.error('🔴 Unhandled Promise Rejection:', reason);
  if (reason?.stack) {
    console.error('Stack:', reason.stack);
  }
});

// Global handler for uncaught errors
window.addEventListener('error', (event) => {
  console.error('🔴 Uncaught Error:', event.error || event.message);
  if (event.error?.stack) {
    console.error('Stack:', event.error.stack);
  }
});

new Vue({
  router,
  store,
  apolloProvider,
  render: (h) => h(App),
}).$mount('#app');
